import initApi from '@/api/initApi';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import { handleApiError } from '@/api/handleApiError.ts';
import type { RequestConfig } from '@/api/Request';
import type { AxiosError } from 'axios';

import type {
  FrameworksApiGetFrameworkInOrganizationV1OrganizationsOrganizationIdFrameworksFrameworkCodeGetRequest,
  PagePlatformControlDetailed,
  PagePlatformFrameworkVersionOverview,
  PagePlatformRequirementDetailed,
  PlatformFrameworksApiCreatePlatformFrameworksPlatformFrameworksPostRequest,
  PlatformFrameworksApiEnableDisablePlatformFrameworkForOrganizationPlatformFrameworksFrameworkCodeOrganizationsOrganizationIdPostRequest,
  PlatformFrameworksApiGetPlatformFrameworkControlsPlatformFrameworksFrameworkCodeControlsGetRequest,
  PlatformFrameworksApiGetPlatformFrameworkPlatformFrameworksFrameworkCodeGetRequest,
  PlatformFrameworksApiGetPlatformFrameworkRequirementsPlatformFrameworksFrameworkCodeRequirementsGetRequest,
  PlatformFrameworksApiGetPlatformFrameworkVersionPlatformFrameworksFrameworkCodeVersionsVersionGetRequest,
  PlatformFrameworksApiGetPlatformFrameworkVersionsPlatformFrameworksFrameworkCodeVersionsGetRequest,
  PlatformFrameworksApiGetUpdatedFrameworksFromZipFilePlatformFrameworksValidatePostRequest,
  PlatformFrameworkVersionOverview,
  ReleaseNotesResponse,
  V1ApiGetFrameworkVersionInOrganizationV1OrganizationsOrganizationIdFrameworksFrameworkCodeVersionsVersionGetRequest,
  V1ApiGetFrameworkVersionsInOrganizationV1OrganizationsOrganizationIdFrameworksFrameworkCodeVersionsGetRequest,
  V1ApiGetPlatformFrameworkControlsV1OrganizationsOrganizationIdFrameworksFrameworkCodeControlsGetRequest,
  V1ApiGetPlatformFrameworkRequirementsV1OrganizationsOrganizationIdFrameworksFrameworkCodeRequirementsGetRequest,
} from '@/clients/v1/index.ts';
import { FrameworksApi, PlatformFrameworksApi, V1Api } from '@/clients/v1/index.ts';

export const useFrameworkStore = defineStore('framework', () => {
  const frameworkData = ref<PlatformFrameworkVersionOverview | null>(null);
  const frameworkControls = ref<PagePlatformControlDetailed | null>(null);
  const frameworkRequirements = ref<PagePlatformRequirementDetailed | null>(null);
  const frameworkLogs = ref<PagePlatformFrameworkVersionOverview | null>(null);

  const zipFile = ref<File | null>(null);
  const validatedZipFile = ref<ReleaseNotesResponse | null>(null);

  const getFramework = async (
    params: FrameworksApiGetFrameworkInOrganizationV1OrganizationsOrganizationIdFrameworksFrameworkCodeGetRequest,
  ) => {
    try {
      const frameworksApi = await initApi(FrameworksApi);
      const response =
        await frameworksApi?.getFrameworkInOrganizationV1OrganizationsOrganizationIdFrameworksFrameworkCodeGet(
          params,
        );

      if (!response) return;

      frameworkData.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getOrganizationFrameworkRequirements = async (
    params: V1ApiGetPlatformFrameworkRequirementsV1OrganizationsOrganizationIdFrameworksFrameworkCodeRequirementsGetRequest,
    options?: RequestConfig,
  ) => {
    try {
      const v1Api = await initApi(V1Api);

      const response =
        await v1Api?.getPlatformFrameworkRequirementsV1OrganizationsOrganizationIdFrameworksFrameworkCodeRequirementsGet(
          params,
          options,
        );

      if (!response) return;

      frameworkRequirements.value = response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getOrganizationFrameworkControls = async (
    params: V1ApiGetPlatformFrameworkControlsV1OrganizationsOrganizationIdFrameworksFrameworkCodeControlsGetRequest,
    options?: RequestConfig,
  ) => {
    try {
      const v1Api = await initApi(V1Api);

      const response =
        await v1Api?.getPlatformFrameworkControlsV1OrganizationsOrganizationIdFrameworksFrameworkCodeControlsGet(
          params,
          options,
        );

      if (!response) return;

      frameworkControls.value = response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getOrganizationFrameworkLogs = async (
    params: V1ApiGetFrameworkVersionsInOrganizationV1OrganizationsOrganizationIdFrameworksFrameworkCodeVersionsGetRequest,
    options?: RequestConfig,
  ) => {
    try {
      const v1Api = await initApi(V1Api);

      const response =
        await v1Api?.getFrameworkVersionsInOrganizationV1OrganizationsOrganizationIdFrameworksFrameworkCodeVersionsGet(
          params,
          options,
        );

      if (!response) return;

      frameworkLogs.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getOrganizationFrameworkVersion = async (
    params: V1ApiGetFrameworkVersionInOrganizationV1OrganizationsOrganizationIdFrameworksFrameworkCodeVersionsVersionGetRequest,
  ) => {
    try {
      const v1Api = await initApi(V1Api);

      const response =
        await v1Api?.getFrameworkVersionInOrganizationV1OrganizationsOrganizationIdFrameworksFrameworkCodeVersionsVersionGet(
          params,
        );

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  // Platform specific

  const validatePlatformFrameworks = async (
    params: PlatformFrameworksApiGetUpdatedFrameworksFromZipFilePlatformFrameworksValidatePostRequest,
  ) => {
    try {
      const platformFrameworksApi = await initApi(PlatformFrameworksApi);

      const response =
        await platformFrameworksApi?.getUpdatedFrameworksFromZipFilePlatformFrameworksValidatePost(
          params,
        );

      if (!response) return;
      zipFile.value = params.file;
      validatedZipFile.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const createPlatformFrameworks = async (
    params: PlatformFrameworksApiCreatePlatformFrameworksPlatformFrameworksPostRequest,
  ) => {
    try {
      const platformFrameworksApi = await initApi(PlatformFrameworksApi);

      const response =
        await platformFrameworksApi?.createPlatformFrameworksPlatformFrameworksPost(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getPlatformFramework = async (
    params: PlatformFrameworksApiGetPlatformFrameworkPlatformFrameworksFrameworkCodeGetRequest,
  ) => {
    try {
      const platformFrameworksApi = await initApi(PlatformFrameworksApi);

      const response =
        await platformFrameworksApi?.getPlatformFrameworkPlatformFrameworksFrameworkCodeGet(params);

      if (!response) return;

      frameworkData.value = response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getPlatformFrameworkControls = async (
    params: PlatformFrameworksApiGetPlatformFrameworkControlsPlatformFrameworksFrameworkCodeControlsGetRequest,
    options?: RequestConfig,
  ) => {
    try {
      const platformFrameworksApi = await initApi(PlatformFrameworksApi);

      const response =
        await platformFrameworksApi?.getPlatformFrameworkControlsPlatformFrameworksFrameworkCodeControlsGet(
          params,
          options,
        );

      if (!response) return;

      frameworkControls.value = response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getPlatformFrameworkRequirements = async (
    params: PlatformFrameworksApiGetPlatformFrameworkRequirementsPlatformFrameworksFrameworkCodeRequirementsGetRequest,
    options?: RequestConfig,
  ) => {
    try {
      const platformFrameworksApi = await initApi(PlatformFrameworksApi);

      const response =
        await platformFrameworksApi?.getPlatformFrameworkRequirementsPlatformFrameworksFrameworkCodeRequirementsGet(
          params,
          options,
        );

      if (!response) return;

      frameworkRequirements.value = response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getPlatformFrameworkLogs = async (
    params: PlatformFrameworksApiGetPlatformFrameworkVersionsPlatformFrameworksFrameworkCodeVersionsGetRequest,
    options?: RequestConfig,
  ) => {
    try {
      const platformFrameworksApi = await initApi(PlatformFrameworksApi);

      const response =
        await platformFrameworksApi?.getPlatformFrameworkVersionsPlatformFrameworksFrameworkCodeVersionsGet(
          params,
          options,
        );

      if (!response) return;

      frameworkLogs.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getPlatformFrameworkVersion = async (
    params: PlatformFrameworksApiGetPlatformFrameworkVersionPlatformFrameworksFrameworkCodeVersionsVersionGetRequest,
  ) => {
    try {
      const platformFrameworksApi = await initApi(PlatformFrameworksApi);

      const response =
        await platformFrameworksApi?.getPlatformFrameworkVersionPlatformFrameworksFrameworkCodeVersionsVersionGet(
          params,
        );

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const toggleFrameworkForOrganization = async (
    params: PlatformFrameworksApiEnableDisablePlatformFrameworkForOrganizationPlatformFrameworksFrameworkCodeOrganizationsOrganizationIdPostRequest,
  ) => {
    try {
      const platformFrameworksApi = await initApi(PlatformFrameworksApi);

      const response =
        await platformFrameworksApi?.enableDisablePlatformFrameworkForOrganizationPlatformFrameworksFrameworkCodeOrganizationsOrganizationIdPost(
          params,
        );

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const resetFrameworkData = () => {
    frameworkData.value = null;
  };

  return {
    frameworkData,
    frameworkControls,
    frameworkRequirements,
    frameworkLogs,
    getFramework,
    getOrganizationFrameworkRequirements,
    getOrganizationFrameworkControls,
    getOrganizationFrameworkLogs,
    getOrganizationFrameworkVersion,
    zipFile,
    validatedZipFile,
    validatePlatformFrameworks,
    createPlatformFrameworks,
    getPlatformFramework,
    getPlatformFrameworkControls,
    getPlatformFrameworkRequirements,
    getPlatformFrameworkLogs,
    getPlatformFrameworkVersion,
    toggleFrameworkForOrganization,
    resetFrameworkData,
  };
});
